import { useMemo } from 'react';
import { PlanStatuses } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { useEmployeeToolbarStore } from '@/modules/employee/Employee/EmployeeTopbar/stores';
import { useEmployerInfoStore } from '@/modules/employee/Employee/PersonalInformation/EmployerInfo/stores';
import { useOrganizationInfoStore } from '@/modules/employee/Employee/PersonalInformation/OrganizationInfo/stores';
import { useGetOrganizationPlansQuery } from '@/modules/employee/Employee/queries/useGetOrganizationPlans.query';
import { useEmployeeEnrollmentsStore } from '@/modules/employee/Employee/stores/useEmployeeEnrollments.store';
import useGetOrgName from '@/modules/employer/queries/useGetOrgName.query';
import { usePlanByIdQuery } from '@/modules/plan/PlanSetup/queries';
import { getIdsFromPath } from '@/utils/modifiers';

interface Props {
  currentPlanId?: string;
  isEdit?: boolean;
}

interface PlanInterface {
  id: string;
  planYear: {
    id: string;
    name: string;
    validFrom: string;
    validTo: string;
  };
  name: string;
  nameState: string;
  accountType: string;
  accountTypeState: string;
  planStatus: string;
  organization_id: string;
  deletable: boolean;
}

export interface CurrentPlanInterface {
  planYear?: string;
  planStartDate?: string;
  planEndDate?: string;
  catchUpAge?: number | null;
  catchUpElection?: number | null;
  contributionMonitoringRequired?: boolean;
  luGpDesignationAllowed?: boolean;
}

export const useGetEnrollmentSetupData = ({ currentPlanId, isEdit }: Props) => {
  const lastName = useEmployeeToolbarStore((state) => state.sourceState?.lastName);
  const defaultFields = useEmployerInfoStore((state) => state.sourceState);
  const organizationInfo = useOrganizationInfoStore((state) => state.sourceState);
  const enrollments = useEmployeeEnrollmentsStore((state) => state.enrollments);
  const isEnrollmentsStateLoading = useEmployeeEnrollmentsStore((state) => state.isLoading);
  const employerOrganizationId = useMemo(() => {
    const { employerId } = getIdsFromPath(organizationInfo?.organizationPath);
    return employerId;
  }, [organizationInfo]);

  const {
    formattedData: plansData = [],
    isFetching: orgPlansLoading,
    isSuccess,
  } = useGetOrganizationPlansQuery(employerOrganizationId || undefined, [
    PlanStatuses.ACTIVE,
    PlanStatuses.PENDING_TERMINATION,
    PlanStatuses.TERMINATION_IN_PROGRESS,
  ]);

  const formattedData = useMemo(() => [...(plansData || [])], [plansData]);

  const { data: plan, isFetching: planLoading } = usePlanByIdQuery(currentPlanId);

  const formattedPlan = useMemo<PlanInterface | null>(() => {
    if (!plan) return null;
    return {
      id: _.toString(plan.id),
      planYear: {
        id: _.toString(plan.plan_year.id),
        name: plan.plan_year.name ?? '',
        validFrom: plan.plan_year.valid_from ?? '',
        validTo: plan.plan_year.valid_to ?? '',
      },
      name: plan.name.name ?? '',
      nameState: plan.name.name_state ?? '',
      accountType: plan.account_type.account_type ?? '',
      accountTypeState: plan.account_type.account_type_state ?? '',
      planStatus: plan.plan_status ?? '',
      organization_id: _.toString(plan.organization_id),
      deletable: !!plan?.deletable,
    };
  }, [plan]);

  const plansPolicyRunout = useMemo(() => {
    return formattedData?.filter((plan) => dayjs(plan?.claimLastDate).isBefore(dayjs(), 'day'));
  }, [formattedData]);

  const isAllPlansHasPolicyRunout = useMemo(() => {
    return plansPolicyRunout?.length === formattedData?.length;
  }, [plansPolicyRunout, formattedData]);

  const availablePlans = useMemo(() => {
    const activePlans = enrollments?.map((item) => item.planId) || null;

    if (isEdit && formattedPlan) {
      return [formattedPlan];
    }

    if (activePlans) {
      const plansWithoutPolicyRunout = formattedData?.filter(
        (plan) => plan?.id && !plansPolicyRunout?.some((p) => p.id === plan.id)
      );
      return plansWithoutPolicyRunout?.filter((plan) => !activePlans.includes(plan.id));
    }

    return formattedData;
  }, [enrollments, formattedData, isEdit, formattedPlan, plansPolicyRunout]);

  const isAddingEnrollmentAvailable = useMemo(() => {
    const activePlans = enrollments?.map((item) => item.planId) || null;

    if (activePlans) {
      return !formattedData?.every((plan) => activePlans.includes(plan.id));
    }

    return !!formattedData?.length;
  }, [enrollments, formattedData]);

  const plansOptions = useMemo(
    () =>
      availablePlans?.map((plan) => ({
        key: plan.id,
        value: plan.name,
        title: plan.name,
      })),
    [availablePlans]
  );

  const minElectionAmount = useMemo(
    () => plan?.plan_primary_config?.min_election_amount_type?.min_election_amount,
    [plan]
  );
  const maxElectionAmount = useMemo(
    () => plan?.plan_primary_config?.max_election_amount_type?.max_election_amount,
    [plan]
  );

  const currentPlan = useMemo<CurrentPlanInterface>(() => {
    const currPlan = formattedData?.find((plan) => plan.id === currentPlanId);

    if (isEdit && formattedPlan) {
      return {
        planYear: formattedPlan.planYear.name,
        planStartDate: plan?.plan_year.valid_from,
        planEndDate: plan?.plan_year.valid_to,
        catchUpAge: plan?.plan_primary_config?.catch_up_age?.catch_up_age ?? null,
        catchUpElection: plan?.plan_primary_config?.catch_up_election?.catch_up_election ?? null,
        contributionMonitoringRequired:
          plan?.plan_account_funding_config?.contribution_monitoring_required
            ?.contribution_monitoring_required,
        luGpDesignationAllowed:
          plan?.plan_primary_config?.lu_gp_designation_allowed?.lu_gp_designation_allowed,
      };
    }

    return {
      planYear: currPlan?.planYear.name,
      planStartDate: plan?.plan_year.valid_from,
      planEndDate: plan?.plan_year.valid_to,
      catchUpAge: plan?.plan_primary_config?.catch_up_age?.catch_up_age ?? null,
      catchUpElection: plan?.plan_primary_config?.catch_up_election?.catch_up_election ?? null,
      contributionMonitoringRequired:
        plan?.plan_account_funding_config?.contribution_monitoring_required?.contribution_monitoring_required,
      luGpDesignationAllowed: plan?.plan_primary_config?.lu_gp_designation_allowed?.lu_gp_designation_allowed,
    };
  }, [currentPlanId, formattedData, plan, isEdit, formattedPlan]);

  const partnerId = organizationInfo?.organizationPath?.split('\\')[2];

  const { name: currentPartnerName, isLoading: orgNameLoading } = useGetOrgName({
    id: partnerId,
    enabled: true,
  });

  const partnerName = useMemo(() => currentPartnerName || '', [currentPartnerName]);
  const employerName = useMemo(() => organizationInfo?.organizationName || '', [organizationInfo]);

  const isEnrollmentsLoading = useMemo(
    () => enrollments && isEnrollmentsStateLoading,
    [enrollments, isEnrollmentsStateLoading]
  );
  const isLoading = useMemo(
    () => Boolean(orgPlansLoading || orgNameLoading || isEnrollmentsLoading),
    [orgNameLoading, orgPlansLoading, isEnrollmentsLoading]
  );

  const SSN = useMemo(() => defaultFields?.ssn, [defaultFields]);
  const EEID = useMemo(() => defaultFields?.employerEmployeeId, [defaultFields]);
  const PEID = useMemo(() => defaultFields?.partnerEmployeeId, [defaultFields]);

  return {
    plan,
    lastName,
    SSN,
    EEID,
    PEID,
    partnerName,
    employerName,
    isLoading,
    isSuccess,
    planLoading,
    plansOptions,
    currentPlan,
    minElectionAmount,
    maxElectionAmount,
    isAddingEnrollmentAvailable,
    isAllPlansHasPolicyRunout,
  };
};
